/**
 * Os documentos que os passos de uma frente exigem (`TP-057`). Moram DENTRO do cartão da frente, ao lado do passo da
 * vez: os `!` do painel de frentes que eram "chave marcada" ganham, para o que é DOCUMENTO, estado, leitura e ações.
 *
 * Três regras da tela, todas vindas do plano:
 *  - `recebido` NÃO é `validado`: rótulos diferentes, e o item só aparece fechado quando o SERVIDOR diz que fechou (D7).
 *  - a leitura mostra o veredito e o motivo em linguagem da operação, nunca o código cru e nunca texto de documento;
 *  - nada sai da plataforma: não há botão que peça nada a ninguém (restrição 9). "O que falta" é só visual.
 *
 * Um item é UMA linha de leitura: o título com um chip de estado, o arquivo (que abre) com o que a leitura disse, e,
 * só se ainda falta algo, o que só uma pessoa valida. Um botão principal por estado; o resto fica em "Mais", para
 * o painel não repetir "Dispensar" em cada item.
 */

import MoreVertOutlined from '@mui/icons-material/MoreVertOutlined';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import { documentosDoCaso, urlDoArquivoDoDocumento } from './api';
import { MetadadosDoDocumento } from './MetadadosDoDocumento';
import { mensagemDeErroDeDocumento, useAcoesDeDocumento } from './acoes';
import {
  ROTULO_DO_MOTIVO,
  type AcaoNoDocumento,
  type DocumentoDoCaso,
  type DocumentoExigido,
} from './tipos';

type TomDoChip = 'default' | 'warning' | 'success';

/** O estado do item em UMA palavra. `recebido` que ainda não fechou é "Falta validar": é o que a pessoa tem a fazer. */
function situacaoDoItem(item: DocumentoExigido): { texto: string; tom: TomDoChip } {
  switch (item.estado) {
    case 'pendente':
      return { texto: 'Pendente', tom: 'default' };
    case 'recebido':
      return item.fechado ? { texto: 'Recebido', tom: 'success' } : { texto: 'Falta validar', tom: 'warning' };
    case 'validado':
      return { texto: 'Validado', tom: 'success' };
    case 'dispensado':
      return { texto: 'Dispensado', tom: 'default' };
    case 'nao_aplicavel':
      return { texto: 'Não se aplica', tom: 'default' };
  }
}

/** O que a leitura disse do arquivo, curto. Sem veredito, o arquivo chegou por outro caminho (a pessoa o ligou ao item). */
function resumoDaLeitura(item: DocumentoExigido): string {
  if (item.veredito === null) return 'sem leitura';
  if (item.veredito === 'confere') return 'a leitura bate com o item';
  const motivo = item.motivo === null ? null : (ROTULO_DO_MOTIVO[item.motivo] ?? null);
  return motivo === null ? 'a leitura não fechou' : `a leitura não fechou: ${motivo}`;
}

/**
 * Validar um item cuja leitura não confirmou (divergiu, foi inconclusiva, ou não teve texto) é um override humano: a
 * pessoa viu o documento e decide por ele. A tela sempre confirma antes, dizendo o que a
 * leitura achou; o caso de "documento de outra pessoa" tem o aviso específico de que os dados dele passam a ser do caso.
 */
function mensagemDeConfirmacaoDeValidacao(item: DocumentoExigido): string {
  if (item.veredito === 'diverge' && item.motivo === 'documento_de_outra_pessoa') {
    return 'A leitura diz que este documento é de outra pessoa. Validar mesmo assim? Os dados dele passam a ser do caso.';
  }
  const resumo = resumoDaLeitura(item);
  return `${resumo.charAt(0).toUpperCase()}${resumo.slice(1)}. Validar mesmo assim?`;
}

/**
 * O último arquivo enviado PARA o item e que não o sustenta. Sem isto, o envio recusado pela leitura (de outra pessoa,
 * de outro item) sumia do item e só reaparecia lá embaixo, entre os documentos soltos.
 */
function ultimoEnvioDoItem(documentos: readonly DocumentoDoCaso[], item: DocumentoExigido): DocumentoDoCaso | null {
  const doItem = documentos
    .filter((d) => d.itemPretendidoId === item.id && d.id !== item.documentoId)
    .sort((a, b) => b.registradoEm.getTime() - a.registradoEm.getTime());
  return doItem[0] ?? null;
}

/** O que aconteceu com o arquivo enviado: esperando, falhou, ou o que a leitura disse dele. */
function situacaoDoEnvio(documento: DocumentoDoCaso): string {
  if (documento.leitura === 'pendente') return 'enviado, aguardando a leitura automática';
  if (documento.leitura === 'falhou') return 'a leitura falhou, validar à mão';
  if (documento.ultimoVeredito === 'confere') return 'a leitura bate com o item';
  const motivo = documento.ultimoMotivo === null ? null : (ROTULO_DO_MOTIVO[documento.ultimoMotivo] ?? null);
  return motivo === null ? 'a leitura não aceitou este arquivo' : `a leitura não aceitou este arquivo: ${motivo}`;
}

export function DocumentosDaFrente({
  casoId,
  frente,
}: {
  casoId: string;
  /** O token da frente (`validacao_pad`...), como a API entrega. */
  frente: string;
}): React.JSX.Element | null {
  const consulta = useQuery(documentosDoCaso(casoId));
  const { agir, enviar } = useAcoesDeDocumento(casoId);

  const itens = (consulta.data?.exigidos ?? []).filter((i) => i.frente === frente);
  if (itens.length === 0) return null;

  const erro = agir.error ?? enviar.error;
  const documentos = consulta.data?.documentos ?? [];
  const fechados = itens.filter((i) => i.fechado).length;

  return (
    <div aria-label="Documentos exigidos">
      <p className="m-0 mb-1 flex items-baseline justify-between gap-2 text-[0.6875rem] uppercase tracking-[0.07em] text-[var(--tinta-3)]">
        <span>Documentos</span>
        <span className="num normal-case tracking-normal">
          {fechados} de {itens.length} {itens.length === 1 ? 'fechado' : 'fechados'}
        </span>
      </p>
      <ul className="m-0 list-none p-0">
        {itens.map((item) => (
          <ItemDeDocumento
            key={item.id}
            casoId={casoId}
            item={item}
            ultimoEnvio={ultimoEnvioDoItem(documentos, item)}
            documentoDoArquivo={documentos.find((d) => d.id === item.documentoId) ?? null}
            lendo={documentos.some((d) => d.itemPretendidoId === item.id && d.leitura === 'pendente')}
            jaEstavaNoCaso={
              !enviar.isPending &&
              enviar.data?.criado === false &&
              !enviar.data.voltouParaLeitura &&
              enviar.variables?.exigidoId === item.id
            }
            enviando={enviar.isPending && enviar.variables.exigidoId === item.id}
            ocupado={agir.isPending || enviar.isPending}
            aoAgir={(acao, motivo, confirmado) => {
              agir.mutate({ exigidoId: item.id, acao, motivo, confirmado });
            }}
            aoEnviar={(arquivo) => {
              enviar.mutate({ arquivo, exigidoId: item.id });
            }}
          />
        ))}
      </ul>
      {erro !== null && (
        <p role="alert" className="mt-2 text-[0.8125rem] text-[var(--roxo)]">
          {mensagemDeErroDeDocumento(erro)}
        </p>
      )}
    </div>
  );
}

function ItemDeDocumento({
  casoId,
  item,
  ultimoEnvio,
  documentoDoArquivo,
  lendo,
  jaEstavaNoCaso,
  enviando,
  ocupado,
  aoAgir,
  aoEnviar,
}: {
  casoId: string;
  item: DocumentoExigido;
  /** O último arquivo enviado PARA este item que não é o que o sustenta: esperando leitura, com falha, ou recusado. */
  ultimoEnvio: DocumentoDoCaso | null;
  /** O que a leitura tirou do arquivo que está no item. */
  documentoDoArquivo: DocumentoDoCaso | null;
  /** Um arquivo enviado para este item ainda espera a leitura: outro só depois dela (o servidor também recusa). */
  lendo: boolean;
  /** O último envio para este item era o MESMO arquivo que já estava no Caso: nada novo aconteceu. */
  jaEstavaNoCaso: boolean;
  /** O envio para este item está a caminho. */
  enviando: boolean;
  ocupado: boolean;
  aoAgir: (acao: AcaoNoDocumento, motivo?: string, confirmado?: boolean) => void;
  aoEnviar: (arquivo: File) => void;
}): React.JSX.Element {
  const [pedindoMotivo, setPedindoMotivo] = useState<'dispensar' | 'reabrir' | null>(null);
  // Validar um item cuja leitura não confirmou é override humano (D2): a tela confirma antes, e o back exige isso.
  const [confirmandoValidacao, setConfirmandoValidacao] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [ancora, setAncora] = useState<HTMLElement | null>(null);
  const seletor = useRef<HTMLInputElement>(null);

  const situacao = situacaoDoItem(item);
  const arquivo =
    item.documentoId !== null && item.nomeDoDocumento !== null
      ? { id: item.documentoId, nome: item.nomeDoDocumento }
      : null;
  const podeConferir = item.documentoId !== null && (item.estado === 'pendente' || item.estado === 'recebido');
  const podeEnviar = item.estado === 'pendente';
  const podeDispensar = item.estado === 'pendente' || item.estado === 'recebido' || item.estado === 'validado';
  const podeReabrir = item.estado === 'recebido' || item.estado === 'validado' || item.estado === 'dispensado';
  const faltaAPessoa = item.exigencias.length > 0 && (item.estado === 'pendente' || item.estado === 'recebido');
  // A leitura não confirmou (divergiu, foi inconclusiva, ficou sem texto...): validar é override humano, e pede confirmação.
  const precisaConfirmarValidacao = item.veredito !== null && item.veredito !== 'confere';

  // UM botão principal por estado. O que sobra (e é raro) vai para "Mais".
  const principal: 'conferir' | 'enviar' | 'reabrir' | null = podeConferir
    ? 'conferir'
    : podeEnviar
      ? 'enviar'
      : item.estado === 'validado' || item.estado === 'dispensado'
        ? 'reabrir'
        : null;
  const noMenu = {
    enviarOutro: podeEnviar && !lendo && principal !== 'enviar',
    dispensar: podeDispensar,
    reabrir: podeReabrir && principal !== 'reabrir',
  };
  const temMenu = noMenu.enviarOutro || noMenu.dispensar || noMenu.reabrir;

  const confirmarMotivo = (): void => {
    if (pedindoMotivo === null) return;
    aoAgir(pedindoMotivo, motivo.trim());
    setPedindoMotivo(null);
    setMotivo('');
  };

  const pedirMotivo = (qual: 'dispensar' | 'reabrir'): void => {
    setAncora(null);
    setPedindoMotivo(qual);
  };

  return (
    <li className="border-0 border-t border-solid border-[var(--fio)] py-2.5 text-[0.8125rem] first:border-t-0 first:pt-1">
      {/* As ações ficam logo abaixo do item, à esquerda, como o botão do passo: perto de onde o olho já está. */}
      <div>
        <div className="min-w-0">
          <p className="m-0 flex flex-wrap items-center gap-x-2 gap-y-1">
            <b>{item.rotulo}</b>
            {!item.obrigatorio && <span className="text-[var(--tinta-2)]">opcional</span>}
            {/* Sem arquivo, o estado é do item; com arquivo, o chip vai para a linha DELE. */}
            {arquivo === null && <Chip size="small" variant="outlined" color={situacao.tom} label={situacao.texto} />}
          </p>

          {arquivo !== null && (
            <p className="m-0 mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[var(--tinta-2)]">
              <a
                className="font-medium text-[var(--navy)] underline-offset-2"
                href={urlDoArquivoDoDocumento(casoId, arquivo.id)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Abrir o documento ${arquivo.nome}`}
              >
                {arquivo.nome}
              </a>
              <Chip size="small" variant="outlined" color={situacao.tom} label={situacao.texto} />
              <span>{resumoDaLeitura(item)}</span>
            </p>
          )}
          {arquivo !== null && <MetadadosDoDocumento documento={documentoDoArquivo} />}

          {/* O que acabou de chegar: sem isto o envio some da tela até a próxima rodada de leitura. */}
          {enviando && <p className="m-0 mt-1 text-[var(--tinta-2)]">Enviando o arquivo…</p>}
          {arquivo === null && ultimoEnvio !== null && (
            <p className="m-0 mt-1 text-[var(--tinta-2)]" role="status">
              <a
                className="font-medium text-[var(--navy)] underline-offset-2"
                href={urlDoArquivoDoDocumento(casoId, ultimoEnvio.id)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Abrir o documento ${ultimoEnvio.nomeArquivo}`}
              >
                {ultimoEnvio.nomeArquivo}
              </a>
              {' · '}
              {situacaoDoEnvio(ultimoEnvio)}
            </p>
          )}
          {arquivo === null && ultimoEnvio !== null && <MetadadosDoDocumento documento={ultimoEnvio} />}
          {jaEstavaNoCaso && (
            <p className="m-0 mt-1 text-[var(--tinta-2)]" role="status">
              Este arquivo já estava no caso: nada novo foi enviado nem lido de novo.
            </p>
          )}

          {item.estado === 'dispensado' && item.motivoDaPessoa !== null && (
            <p className="m-0 mt-1 text-[var(--tinta-2)]">Motivo: {item.motivoDaPessoa}</p>
          )}

          {/* O texto do PDF não prova carimbo nem assinatura (são imagem; o rótulo vem impresso até no formulário em
              branco, T11): a tela diz o que a pessoa valida, e não mostra o indício da leitura como se fosse evidência. */}
          {faltaAPessoa && (
            <p className="m-0 mt-1 text-[var(--tinta-2)]">
              Só uma pessoa valida: {item.exigencias.map((e) => e.descricao.toLowerCase()).join('; ')}
            </p>
          )}
        </div>

        {pedindoMotivo === null && !confirmandoValidacao && (
          <div className="mt-1 flex items-center gap-1">
            {principal === 'conferir' && (
              <Button
                size="small"
                variant="contained"
                disabled={ocupado}
                aria-label={`Validar ${item.rotulo}`}
                onClick={() => {
                  if (precisaConfirmarValidacao) setConfirmandoValidacao(true);
                  else aoAgir('conferir');
                }}
              >
                Validar
              </Button>
            )}
            {principal === 'enviar' && (
              <Button
                size="small"
                variant="outlined"
                disabled={ocupado || lendo}
                aria-label={`Enviar documento: ${item.rotulo}`}
                onClick={() => {
                  seletor.current?.click();
                }}
              >
                Enviar
              </Button>
            )}
            {principal === 'reabrir' && (
              <Button
                size="small"
                variant="outlined"
                disabled={ocupado}
                aria-label={`Reabrir ${item.rotulo}`}
                onClick={() => {
                  pedirMotivo('reabrir');
                }}
              >
                Reabrir
              </Button>
            )}
            {temMenu && (
              <>
                <IconButton
                  size="small"
                  disabled={ocupado}
                  aria-label={`Mais ações de ${item.rotulo}`}
                  aria-haspopup="menu"
                  onClick={(e) => {
                    setAncora(e.currentTarget);
                  }}
                >
                  <MoreVertOutlined fontSize="small" />
                </IconButton>
                <Menu
                  anchorEl={ancora}
                  open={ancora !== null}
                  onClose={() => {
                    setAncora(null);
                  }}
                >
                  {noMenu.enviarOutro && (
                    <MenuItem
                      onClick={() => {
                        setAncora(null);
                        seletor.current?.click();
                      }}
                    >
                      Enviar outro arquivo
                    </MenuItem>
                  )}
                  {noMenu.reabrir && (
                    <MenuItem
                      onClick={() => {
                        pedirMotivo('reabrir');
                      }}
                    >
                      Reabrir
                    </MenuItem>
                  )}
                  {noMenu.dispensar && (
                    <MenuItem
                      onClick={() => {
                        pedirMotivo('dispensar');
                      }}
                    >
                      Dispensar
                    </MenuItem>
                  )}
                </Menu>
              </>
            )}
          </div>
        )}
      </div>

      <input
        ref={seletor}
        type="file"
        hidden
        aria-label={`Arquivo de ${item.rotulo}`}
        onChange={(e) => {
          const arquivo = e.target.files?.[0];
          if (arquivo !== undefined) aoEnviar(arquivo);
          e.target.value = '';
        }}
      />

      {confirmandoValidacao && (
        <div className="mt-2 space-y-2" role="alertdialog" aria-label="Confirmar validação">
          <p className="m-0 text-[var(--tinta)]">{mensagemDeConfirmacaoDeValidacao(item)}</p>
          <div className="flex gap-2">
            <Button
              size="small"
              variant="contained"
              disabled={ocupado}
              onClick={() => {
                setConfirmandoValidacao(false);
                aoAgir('conferir', undefined, true);
              }}
            >
              Validar mesmo assim
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={() => {
                setConfirmandoValidacao(false);
              }}
            >
              Cancelar
            </Button>
          </div>
        </div>
      )}

      {pedindoMotivo !== null && (
        <div className="mt-2 space-y-2">
          <TextField
            size="small"
            fullWidth
            autoFocus
            label={`Motivo para ${pedindoMotivo === 'dispensar' ? 'dispensar' : 'reabrir'}`}
            value={motivo}
            onChange={(e) => {
              setMotivo(e.target.value);
            }}
          />
          <div className="flex gap-2">
            <Button
              size="small"
              variant="outlined"
              disabled={ocupado || motivo.trim() === ''}
              onClick={confirmarMotivo}
            >
              {pedindoMotivo === 'dispensar' ? 'Confirmar dispensa' : 'Confirmar reabertura'}
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={() => {
                setPedindoMotivo(null);
                setMotivo('');
              }}
            >
              Cancelar
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}
